<script setup lang="ts">
/**
 * A task's KPIs with what the viewer may do to each (docs/performance.md, Signing off a KPI). Any person who may edit
 * the task signs a KPI off, setting its target, cadence and source as they do; until then it is a proposal and draws
 * no light. They can withdraw one too, signed off or not. Everyone here is a person in their own session: an agent's
 * token, which may propose a KPI, never signs one off or moves one that is.
 *
 * The server decides all of this again on every call (`TaskKpisService`): the buttons here only offer what it allows.
 */
import type { KpiSourceKind } from '@zerohuman/shared'
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhConfirmButton,
    ZhPanel,
} from '@zerohuman/ui'
import { computed, reactive, ref, useId } from 'vue'
import { api } from '@/api'
import { type EnterpriseUserRow, usersApi } from '@/api-users'
import TaskKpiRows from '@/components/TaskKpiRows.vue'
import { KPI_DIRECTION_OPTIONS } from '@/lib/kpi'
import { pickedId } from '@/lib/memberPickers'
import { type TaskKpi, type TaskKpiSignOff, taskKpisApi } from '@/task-kpis-api'

const props = defineProps<{
    taskId: string
    kpis: readonly TaskKpi[]
    /** The viewer may edit this task's definition. */
    canEdit: boolean
    /**
     * The task version the page read (#1427). A sign-off that changes the KPI and a withdrawal each publish a version,
     * so each names the one it was made from, and is refused when the task has moved since.
     */
    baseVersionId?: string | null
}>()

const emit = defineEmits<{ changed: [kpis: TaskKpi[]] }>()

/** How often a KPI is read, as the form offers it. A cadence set some other way is kept and offered too. */
const CADENCES: ReadonlyArray<{ minutes: number; label: string }> = [
    { minutes: 60, label: 'Hourly' },
    { minutes: 1440, label: 'Daily' },
    { minutes: 10080, label: 'Weekly' },
    { minutes: 43200, label: 'Every 30 days' },
]

const openId = ref<string | null>(null)
const busy = ref(false)
const error = ref<string | null>(null)
const tasks = ref<Array<{ id: string; name: string }>>([])
const people = ref<EnterpriseUserRow[]>([])
// A number input's v-model hands back a number once typed into, so both are read through `String()`.
const form = reactive({
    targetValue: '' as string | number,
    targetDirection: 'at_least' as 'at_least' | 'at_most',
    amberBand: '' as string | number,
    cadenceMinutes: 10080,
    sourceKind: 'task' as KpiSourceKind,
    sourceRef: '',
})

const open = computed(() => props.kpis.find(kpi => kpi.id === openId.value) ?? null)
const cadences = computed(() =>
    CADENCES.some(option => option.minutes === form.cadenceMinutes)
        ? CADENCES
        : [...CADENCES, { minutes: form.cadenceMinutes, label: `Every ${form.cadenceMinutes} minutes` }],
)
const sources = computed(() =>
    form.sourceKind === 'task'
        ? tasks.value.map(task => ({ id: task.id, name: task.name }))
        : people.value.map(person => ({ id: person.id, name: person.name })),
)

/*
 * The sign-off form's controls are the kit's (#1227): the numbers are the kit `Input`, the choices the kit `Select`,
 * on a kit surface. A Select's trigger is a button, so it is named by the words beside it rather than wrapped in a
 * label. No source chosen is held as no value at all, so the source picker reads its placeholder.
 */
const fieldId = useId()
const sourceChoice = computed(() => form.sourceRef || undefined)

/** A new kind of source lists other sources, so whichever was chosen goes with the old kind. */
function chooseSourceKind(choice: unknown): void {
    const kind = choice as KpiSourceKind
    if (kind !== form.sourceKind) {
        form.sourceKind = kind
        form.sourceRef = ''
    }
}

function chooseSource(choice: unknown): void {
    form.sourceRef = pickedId(choice)
}

/** Opens the form on `kpi`, filled with what it already has, and loads who and what can be its source. */
async function startSignOff(kpi: TaskKpi): Promise<void> {
    openId.value = kpi.id
    error.value = null
    form.targetValue = kpi.targetValue === null ? '' : String(kpi.targetValue)
    form.targetDirection = kpi.targetDirection ?? 'at_least'
    form.amberBand = kpi.amberBand === null ? '' : String(kpi.amberBand)
    form.cadenceMinutes = kpi.cadenceMinutes ?? 10080
    form.sourceKind = kpi.sourceKind ?? 'task'
    form.sourceRef = kpi.sourceRef ?? (kpi.sourceKind ? '' : props.taskId)
    const [taskRows, userRows] = await Promise.all([
        api.tasks({ limit: 200 }).catch(() => null),
        usersApi.users().catch(() => []),
    ])
    tasks.value = (taskRows?.data ?? []).map(task => ({ id: task.id, name: task.name ?? task.slug }))
    people.value = userRows
}

/** The version the page read, as a change's last argument, and nothing when the page has none to name. */
function readFrom(): [baseVersionId?: string] {
    return props.baseVersionId ? [props.baseVersionId] : []
}

/** A refusal in words the person can act on; anything unexpected is shown as the server said it. */
function describe(message: string): string {
    if (message.startsWith('kpi_sign_off_incomplete:')) {
        const missing = message.slice('kpi_sign_off_incomplete:'.length).split(',').join(', ')
        return `Set the ${missing} before signing off.`
    }
    if (message === 'task_kpi_source_not_found') {
        return 'That source is not in this enterprise any more. Choose another.'
    }
    return message
}

async function signOff(): Promise<void> {
    const kpi = open.value
    const target = String(form.targetValue).trim()
    const band = String(form.amberBand).trim()
    if (!kpi || busy.value) {
        return
    }
    if (target === '' || !Number.isFinite(Number(target)) || !form.sourceRef) {
        error.value = 'Set a target and a source before signing off.'
        return
    }
    busy.value = true
    error.value = null
    try {
        const fields: TaskKpiSignOff = {
            targetValue: Number(target),
            targetDirection: form.targetDirection,
            amberBand: band === '' ? null : Number(band),
            cadenceMinutes: form.cadenceMinutes,
            sourceKind: form.sourceKind,
            sourceRef: form.sourceRef,
        }
        const change = await taskKpisApi.signOff(props.taskId, kpi.id, fields, ...readFrom())
        openId.value = null
        emit('changed', change.kpis)
    } catch (caught) {
        error.value = describe(caught instanceof Error ? caught.message : String(caught))
    } finally {
        busy.value = false
    }
}

async function withdraw(kpi: TaskKpi): Promise<void> {
    if (busy.value) {
        return
    }
    busy.value = true
    error.value = null
    try {
        const change = await taskKpisApi.withdraw(props.taskId, kpi.id, ...readFrom())
        if (openId.value === kpi.id) {
            openId.value = null
        }
        emit('changed', change.kpis)
    } catch (caught) {
        error.value = describe(caught instanceof Error ? caught.message : String(caught))
    } finally {
        busy.value = false
    }
}
</script>

<template>
	<div>
		<TaskKpiRows :kpis="kpis">
			<template #actions="{ kpi }">
				<span class="flex gap-2">
					<Button
						v-if="canEdit && !kpi.signedOff"
						type="button"
						size="sm"
						variant="outline"
						:data-testid="`task-kpi-sign-off-${kpi.id}`"
						:disabled="busy"
						@click="startSignOff(kpi)"
					>
						Sign off
					</Button>
					<ZhConfirmButton
						v-if="canEdit"
						label="Withdraw"
						confirm-label="Withdraw KPI"
						:title="`Withdraw ${kpi.name}?`"
						description="The task gets a new version without this KPI. Earlier versions keep it, and so does its reading history."
						:disabled="busy"
						:data-testid="`task-kpi-withdraw-${kpi.id}`"
						@confirm="withdraw(kpi)"
					/>
				</span>
			</template>
		</TaskKpiRows>

		<ZhPanel
			v-if="open"
			as="form"
			data-testid="task-kpi-sign-off-form"
			class="mt-3 grid gap-3 sm:grid-cols-2"
			@submit.prevent="signOff"
		>
			<p class="text-sm font-medium sm:col-span-2">Sign off “{{ open.name }}”</p>
			<div class="grid gap-1 text-sm">
				<span :id="`${fieldId}-target`">Target</span>
				<span class="flex gap-2">
					<Select v-model="form.targetDirection" data-testid="sign-off-direction">
						<SelectTrigger :aria-labelledby="`${fieldId}-target`">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="direction in KPI_DIRECTION_OPTIONS" :key="direction.value" :value="direction.value">
								{{ direction.label }}
							</SelectItem>
						</SelectContent>
					</Select>
					<Input
						v-model="form.targetValue"
						data-testid="sign-off-target"
						type="number"
						step="any"
						required
						:aria-labelledby="`${fieldId}-target`"
					/>
				</span>
			</div>
			<label class="grid gap-1 text-sm">
				AMBER band (optional)
				<Input v-model="form.amberBand" data-testid="sign-off-band" type="number" step="any" min="0" />
			</label>
			<div class="grid gap-1 text-sm">
				<span :id="`${fieldId}-cadence`">Read</span>
				<Select v-model="form.cadenceMinutes" data-testid="sign-off-cadence">
					<SelectTrigger class="w-full" :aria-labelledby="`${fieldId}-cadence`">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem v-for="cadence in cadences" :key="cadence.minutes" :value="cadence.minutes">
							{{ cadence.label }}
						</SelectItem>
					</SelectContent>
				</Select>
			</div>
			<div class="grid gap-1 text-sm">
				<span :id="`${fieldId}-source`">Source</span>
				<span class="flex gap-2">
					<Select
						:model-value="form.sourceKind"
						data-testid="sign-off-source-kind"
						@update:model-value="chooseSourceKind"
					>
						<SelectTrigger :aria-labelledby="`${fieldId}-source`">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="task">A task records it</SelectItem>
							<SelectItem value="person">A person enters it</SelectItem>
						</SelectContent>
					</Select>
					<!-- Named, so the form carries Reka's own native select and the browser still refuses no source. -->
					<Select
						:model-value="sourceChoice"
						name="sourceRef"
						required
						data-testid="sign-off-source"
						@update:model-value="chooseSource"
					>
						<SelectTrigger class="w-full" :aria-labelledby="`${fieldId}-source`">
							<SelectValue placeholder="Choose…" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="source in sources" :key="source.id" :value="source.id">{{ source.name }}</SelectItem>
						</SelectContent>
					</Select>
				</span>
			</div>
			<div class="flex gap-2 sm:col-span-2">
				<Button type="submit" size="sm" data-testid="sign-off-submit" :disabled="busy">Sign off</Button>
				<Button type="button" size="sm" variant="outline" :disabled="busy" @click="openId = null">Cancel</Button>
			</div>
		</ZhPanel>
		<p v-if="error" data-testid="task-kpi-review-error" class="text-destructive mt-2 text-sm">{{ error }}</p>
	</div>
</template>
